import type {
  ServerMessage,
  WebSocketConnectionState,
  WebSocketMessageHandler,
} from '@acitra/shared';
import { getConfig } from '../config';

/**
 * WebSocket service for real-time updates
 *
 * Handles connection management, reconnection with exponential backoff,
 * and message subscription/dispatch.
 */
class WebSocketService {
  private ws: WebSocket | null = null;
  private connectionState: WebSocketConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private subscribers = new Map<string, Set<WebSocketMessageHandler>>();
  private stateListeners = new Set<(state: WebSocketConnectionState) => void>();

  // Connection parameters (set on connect)
  private token: string | null = null;
  private tenantId: string | null = null;

  // Configuration
  private readonly maxReconnectAttempts = 10;
  private readonly baseReconnectDelay = 1000; // 1 second
  private readonly maxReconnectDelay = 30000; // 30 seconds
  private readonly pingIntervalMs = 30000; // 30 seconds

  /**
   * Connect to the WebSocket server
   */
  connect(token: string, tenantId: string): void {
    // Store connection parameters for reconnection
    this.token = token;
    this.tenantId = tenantId;

    // Don't reconnect if already connected or connecting
    if (this.connectionState === 'connected' || this.connectionState === 'connecting') {
      return;
    }

    this.doConnect();
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.clearTimers();
    this.token = null;
    this.tenantId = null;
    this.reconnectAttempts = 0;

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.setConnectionState('disconnected');
  }

  /**
   * Subscribe to a specific message type
   * Returns an unsubscribe function
   */
  subscribe<T = unknown>(type: string, handler: WebSocketMessageHandler<T>): () => void {
    if (!this.subscribers.has(type)) {
      this.subscribers.set(type, new Set());
    }
    this.subscribers.get(type)!.add(handler as WebSocketMessageHandler);

    // Return unsubscribe function
    return () => {
      const handlers = this.subscribers.get(type);
      if (handlers) {
        handlers.delete(handler as WebSocketMessageHandler);
        if (handlers.size === 0) {
          this.subscribers.delete(type);
        }
      }
    };
  }

  /**
   * Subscribe to connection state changes
   */
  onStateChange(listener: (state: WebSocketConnectionState) => void): () => void {
    this.stateListeners.add(listener);
    // Immediately notify of current state
    listener(this.connectionState);

    return () => {
      this.stateListeners.delete(listener);
    };
  }

  /**
   * Get current connection state
   */
  getState(): WebSocketConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  // Private methods

  private doConnect(): void {
    const config = getConfig();
    if (!config?.wsUrl || !this.token || !this.tenantId) {
      console.warn('[WebSocket] Cannot connect: missing config, token, or tenantId');
      return;
    }

    this.setConnectionState('connecting');

    try {
      const url = `${config.wsUrl}?token=${encodeURIComponent(this.token)}&tenantId=${encodeURIComponent(this.tenantId)}`;
      this.ws = new WebSocket(url);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
    } catch (error) {
      console.error('[WebSocket] Connection error:', error);
      this.scheduleReconnect();
    }
  }

  private handleOpen(): void {
    console.log('[WebSocket] Connected');
    this.setConnectionState('connected');
    this.reconnectAttempts = 0;
    this.startPingInterval();
  }

  private handleClose(event: CloseEvent): void {
    console.log('[WebSocket] Disconnected:', event.code, event.reason);
    this.clearTimers();
    this.ws = null;

    // Don't reconnect if intentionally closed or no credentials
    if (event.code === 1000 || !this.token || !this.tenantId) {
      this.setConnectionState('disconnected');
      return;
    }

    // Schedule reconnection
    this.scheduleReconnect();
  }

  private handleError(event: Event): void {
    console.error('[WebSocket] Error:', event);
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: ServerMessage = JSON.parse(event.data);

      // Handle pong internally
      if (message.type === 'pong') {
        return;
      }

      // Dispatch to subscribers
      const handlers = this.subscribers.get(message.type);
      if (handlers) {
        handlers.forEach((handler) => {
          try {
            handler(message.data);
          } catch (error) {
            console.error('[WebSocket] Handler error:', error);
          }
        });
      }

      // Also dispatch to wildcard subscribers ('*')
      const wildcardHandlers = this.subscribers.get('*');
      if (wildcardHandlers) {
        wildcardHandlers.forEach((handler) => {
          try {
            handler(message);
          } catch (error) {
            console.error('[WebSocket] Wildcard handler error:', error);
          }
        });
      }
    } catch (error) {
      console.error('[WebSocket] Failed to parse message:', error);
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn('[WebSocket] Max reconnect attempts reached');
      this.setConnectionState('disconnected');
      return;
    }

    this.setConnectionState('reconnecting');

    // Exponential backoff with jitter
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts) + Math.random() * 1000,
      this.maxReconnectDelay
    );

    console.log(
      `[WebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts + 1})`
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.doConnect();
    }, delay);
  }

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.pingIntervalMs);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private setConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.stateListeners.forEach((listener) => {
        try {
          listener(state);
        } catch (error) {
          console.error('[WebSocket] State listener error:', error);
        }
      });
    }
  }
}

// Export singleton instance
export const wsService = new WebSocketService();
